import React from "react";

import Input from "@common/designSystem/Input";

import IconButton from "@replo/design-system/components/button/IconButton";
import SelectableChip from "@replo/design-system/components/chip/SelectableChip";
import Tooltip from "@replo/design-system/components/tooltip/Tooltip";
import twMerge from "@replo/design-system/utils/twMerge";
import {
  CalendarClock,
  Download,
  GraduationCap,
  Handshake,
  Info,
  LockOpen,
  Repeat,
  Shirt,
  Store,
} from "lucide-react";

type ProductDescription =
  | "physical"
  | "software"
  | "digital"
  | "membership"
  | "courses"
  | "professional"
  | "events"
  | "retail";

type AnnualRevenue =
  | "0-500k"
  | "500k-2m"
  | "2m-5m"
  | "5m-10m"
  | "10m-50m"
  | "50m+";

interface OnboardingShopInfoContentProps {
  shopName: string;
  setShopName: (value: string) => void;
  subdomain: string;
  setSubdomain: (value: string) => void;
  productDescription: ProductDescription | null;
  setProductDescription: (value: ProductDescription) => void;
  annualRevenue: AnnualRevenue | null;
  setAnnualRevenue: (value: AnnualRevenue) => void;
  isBusinessContext?: boolean;
}

const OnboardingShopInfoContent: React.FC<OnboardingShopInfoContentProps> = ({
  shopName,
  setShopName,
  subdomain,
  setSubdomain,
  productDescription,
  setProductDescription,
  annualRevenue,
  setAnnualRevenue,
  isBusinessContext = true,
}) => {
  return (
    <div className="flex flex-col gap-10">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
        <div className="flex flex-col gap-3">
          <label className="text-default text-sm md:text-base font-medium flex items-center h-6">
            <span>
              {isBusinessContext
                ? "What's the name of your business?"
                : "What's the name of your client's business?"}{" "}
              {shopName === "" && <span className="text-danger">*</span>}
            </span>
          </label>
          <Input
            type="text"
            value={shopName}
            onChange={(e) => setShopName(e.target.value)}
            size="base"
            placeholder="Enter your shop's name"
          />
        </div>

        <div className="flex flex-col gap-3">
          <label className="text-default text-sm md:text-base font-medium flex items-center justify-between h-6">
            <span>
              {isBusinessContext
                ? "Confirm your subdomain"
                : "Choose a name for your storefront URL"}{" "}
              {subdomain === "" && <span className="text-danger">*</span>}
            </span>
            <Tooltip
              content={
                <div className="flex flex-col gap-1 max-w-[200px] py-1 rounded-md">
                  <div className="font-semibold">Domains in Replo</div>
                  <div>
                    Heads up: this can't be changed later, but you can add a
                    custom domain at any time!
                  </div>
                </div>
              }
              triggerAsChild
              side="right"
            >
              <IconButton
                variant="tertiary"
                icon={<Info size={14} />}
                size="sm"
                layoutClassName="w-4 h-4"
                UNSAFE_className="text-muted hover:text-default"
              />
            </Tooltip>
          </label>
          <div className="bg-sheet justify-content-around text-default border-0.5 border-border-component relative flex items-center rounded-md px-[1px] transition-all duration-75 focus-within:border-transparent h-8 max-sm:h-10">
            <div className="justify-content-around relative flex h-full flex-1 grow items-center rounded-md transition-all duration-75 focus-within:ring-primary focus-within:ring-1 focus-within:ring-inset gap-2 px-2">
              <Input
                type="text"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value)}
                size="base"
                placeholder="Enter your subdomain"
                unsafe_className="border-0 bg-transparent focus-within:ring-0 p-0"
              />
            </div>
            <div className="bg-surface px-3 text-muted text-sm flex items-center border-l border-border h-full rounded-r-md">
              .reploshops.com
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-default text-sm md:text-base font-medium flex items-center justify-between">
          <span>
            {isBusinessContext
              ? "How do you make money?"
              : "How does your client make money?"}{" "}
            {productDescription === null && (
              <span className="text-danger">*</span>
            )}
          </span>
          <Tooltip
            content={
              <div className="flex flex-col gap-1 max-w-[200px] py-1 rounded-md">
                <div className="font-semibold">
                  Why does Replo need this info?
                </div>
                <div>
                  We use your selection to customize your starting templates—so
                  you can hit the ground running.
                </div>
              </div>
            }
            triggerAsChild
            side="right"
          >
            <IconButton
              variant="tertiary"
              icon={<Info size={14} />}
              size="sm"
              layoutClassName="w-4 h-4"
              UNSAFE_className="text-muted hover:text-default"
            />
          </Tooltip>
        </label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "physical"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("physical")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "physical" ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "physical" ? (
                <img
                  src="/images/onboarding/physical.png"
                  alt="Physical"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<Shirt size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "physical" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "physical"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Physical
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "physical" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "physical"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Sell shippable, physical goods with external checkout links.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "software"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("software")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "software" ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "software" ? (
                <img
                  src="/images/onboarding/software.png"
                  alt="Software"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<Repeat size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "software" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "software"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Software
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "software" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "software"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Sell software or apps that are one-time or subscriptions.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "digital"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("digital")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute inset-0 w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "digital" ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "digital" ? (
                <img
                  src="/images/onboarding/digital.png"
                  alt="Digital"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<Download size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "digital" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "digital" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Digital
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "digital" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "digital" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Sell assets like designs, PDFs, or files, delivered by
                  download.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "membership"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("membership")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "membership"
                  ? "opacity-100"
                  : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "membership" ? (
                <img
                  src="/images/onboarding/membership.png"
                  alt="Membership"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<LockOpen size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "membership" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "membership"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Membership
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "membership" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "membership"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Sell access to viewing gated online content.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "courses"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("courses")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "courses" ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "courses" ? (
                <img
                  src="/images/onboarding/courses.png"
                  alt="Courses"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<GraduationCap size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "courses" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "courses" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Courses
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "courses" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "courses" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Sell structured lessons delivered via platform page.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "professional"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("professional")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "professional"
                  ? "opacity-100"
                  : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "professional" ? (
                <img
                  src="/images/onboarding/professional-services.png"
                  alt="Professional Services"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<Handshake size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "professional" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "professional"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Professional Services
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "professional" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "professional"
                      ? { color: "#F5FAD9" }
                      : {}
                  }
                >
                  Consulting, calendars, coaching, freelance work, or expert
                  help.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "events"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("events")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "events" ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "events" ? (
                <img
                  src="/images/onboarding/events.png"
                  alt="Events & Experiences"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<CalendarClock size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "events" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "events" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Events & Experiences
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "events" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "events" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Live webinars, workshops, classes, or in-person experiences.
                </div>
              </div>
            </div>
          </div>
          <div
            className={twMerge(
              "border rounded-lg p-4 relative cursor-pointer transition-colors duration-300",
              productDescription === "retail"
                ? "border-transparent"
                : "border-border",
            )}
            onClick={() => setProductDescription("retail")}
          >
            <img
              src="/images/onboarding/product-card-bg.png"
              alt=""
              className={twMerge(
                "absolute -inset-px w-full h-full object-cover rounded-lg transition-opacity duration-300",
                productDescription === "retail" ? "opacity-100" : "opacity-0",
              )}
            />
            <div className="flex flex-col gap-2 relative z-10">
              {productDescription === "retail" ? (
                <img
                  src="/images/onboarding/retail.png"
                  alt="Local Retail"
                  className="w-8 h-8"
                />
              ) : (
                <IconButton
                  variant="tertiary"
                  icon={<Store size={16} />}
                  size="base"
                  layoutClassName="w-8 h-8"
                  UNSAFE_className="bg-sheet border border-border rounded-md"
                />
              )}
              <div className="flex flex-col gap-1">
                <div
                  className={twMerge(
                    "text-base font-semibold transition-colors duration-300",
                    productDescription === "retail" ? "" : "text-default",
                  )}
                  style={
                    productDescription === "retail" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Local Retail
                </div>
                <div
                  className={twMerge(
                    "text-sm transition-colors duration-300",
                    productDescription === "retail" ? "" : "text-muted",
                  )}
                  style={
                    productDescription === "retail" ? { color: "#F5FAD9" } : {}
                  }
                >
                  Sell locally at cafes, boutiques, food & beverage vendors.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <label className="text-default text-sm md:text-base font-medium">
          {isBusinessContext
            ? "What's your annual revenue?"
            : "What's your client's annual revenue?"}{" "}
          {annualRevenue === null && <span className="text-danger">*</span>}
        </label>
        <div className="flex flex-wrap gap-4">
          <SelectableChip
            size="base"
            active={annualRevenue === "0-500k"}
            onChange={() => setAnnualRevenue("0-500k")}
            UNSAFE_activeClassName="bg-black text-white border-black hover:bg-black hover:text-white"
            showHoverStateWhenActive={false}
          >
            $0 - $500K
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "500k-2m"}
            onChange={() => setAnnualRevenue("500k-2m")}
            UNSAFE_activeClassName="bg-black text-white border-black hover:bg-black hover:text-white"
            showHoverStateWhenActive={false}
          >
            $500K - $2M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "2m-5m"}
            onChange={() => setAnnualRevenue("2m-5m")}
            UNSAFE_activeClassName="bg-black text-white border-black hover:bg-black hover:text-white"
            showHoverStateWhenActive={false}
          >
            $2M - $5M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "5m-10m"}
            onChange={() => setAnnualRevenue("5m-10m")}
            UNSAFE_activeClassName="bg-black text-white border-black hover:bg-black hover:text-white"
            showHoverStateWhenActive={false}
          >
            $5M - $10M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "10m-50m"}
            onChange={() => setAnnualRevenue("10m-50m")}
            UNSAFE_activeClassName="bg-black text-white border-black hover:bg-black hover:text-white"
            showHoverStateWhenActive={false}
          >
            $10M - $50M
          </SelectableChip>
          <SelectableChip
            size="base"
            active={annualRevenue === "50m+"}
            onChange={() => setAnnualRevenue("50m+")}
            UNSAFE_activeClassName="bg-black text-white border-black hover:bg-black hover:text-white"
            showHoverStateWhenActive={false}
          >
            $50M+
          </SelectableChip>
        </div>
      </div>
    </div>
  );
};

export default OnboardingShopInfoContent;
export type { ProductDescription, AnnualRevenue };
